// src/components/content/companylist/CompanyCard.jsx
import React from "react";
import {
  Box,
  Stack,
  Heading,
  Text,
  Badge,
  HStack,
  Button,
  Icon,
  Flex,
  Image,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { CheckCircle, ExternalLink, Star, Award } from "lucide-react";

/**
 * CompanyCard
 * Props:
 *  - item: {
 *      key, name, slug, tagline, specialties[], phone, callrailNumber,
 *      siteUrl, ratingAggregate, reviewCount, logo, featured?
 *    }
 *  - rank?: number
 *  - onContact?: (item) => void
 */
export default function CompanyCard({ item, rank, onContact }) {
  const bodyColor = { base: "gray.800", _dark: "gray.100" };
  const ACCENTS = {
    TAG: { from: "blue.600", to: "cyan.400", chip: "blue" },
    WYNN: { from: "purple.600", to: "pink.400", chip: "purple" },
    ATS: { from: "orange.500", to: "yellow.400", chip: "orange" },
    AMITY: { from: "teal.600", to: "green.400", chip: "teal" },
    TGC: { from: "red.600", to: "rose.400", chip: "red" },
  };
  const accent = ACCENTS[item?.key] || {
    from: "blue.600",
    to: "cyan.400",
    chip: "blue",
  };

  // neutrals
  const cardBg = { base: "white", _dark: "gray.800" };
  const sub = { base: "gray.600", _dark: "gray.300" };

  // Specialties -> readable chips
  const SPECIALTY_LABELS = {
    individual_1040: "Individual (1040)",
    small_business: "Small Business",
    business: "Business",
    complex_corporate: "Complex Corporate",
    payroll_941: "Payroll / 941",
    truckers_1099: "Truckers & 1099",
  };
  const chips = Array.isArray(item?.specialties)
    ? item.specialties.map((k) => SPECIALTY_LABELS[k] || k).filter(Boolean)
    : [];

  // rating stars
  const stars = [];
  const rating =
    typeof item?.ratingAggregate === "number"
      ? Math.max(0, Math.min(5, item.ratingAggregate))
      : null;
  if (rating !== null) {
    const full = Math.floor(rating);
    const hasHalf = rating - full >= 0.5 && rating < 5;
    for (let i = 0; i < 5; i++) {
      const filled = i < full || (i === full && hasHalf);
      stars.push(
        <Icon
          as={Star}
          key={i}
          boxSize="18px"
          color={filled ? "yellow" : "whiteAlpha.700"}
          style={filled ? { fill: "currentColor" } : {}}
        />
      );
    }
  }

  return (
    <Box
      position="relative"
      maxW="800px"
      w="100%"
      borderWidth="1px"
      borderColor={accent.from}
      borderRadius="xl"
      bg={cardBg}
      overflow="hidden"
      transition="transform 0.2s ease, box-shadow 0.2s ease"
      _hover={{ transform: "translateY(-4px)", boxShadow: "xl" }}
    >
      {/* Featured ribbon */}
      {item?.featured ? (
        <Box
          position="absolute"
          top="12px"
          right="-44px"
          transform="rotate(45deg)"
          bgGradient={`linear(to-r, ${accent.from}, ${accent.to})`}
          px="56px"
          py="6px"
          fontWeight="bold"
          fontSize="s"
          textTransform="uppercase"
          letterSpacing="widest"
          shadow="md"
          zIndex={2}
        >
          <Flex align="center" gap="1" ml="8">
            <Icon as={Award} boxSize="14px" />
            Featured
          </Flex>
        </Box>
      ) : null}

      {/* COLORFUL HEADER */}
      <Box
        bgGradient={`linear(to-r, ${accent.from}, ${accent.to})`}
        px={5}
        py={4}
      >
        <HStack justify="space-between" align="center" spacing={4}>
          <HStack spacing={3} align="center">
            {typeof rank === "number" ? (
              <Badge
                bg="whiteAlpha.300"
                fontSize="2rem"
                py={0.5}
                px={2}
                borderRadius="md"
              >
                #{rank}
              </Badge>
            ) : null}

            {item?.logo ? (
              <Image
                src={item.logo}
                alt={`${item.name} logo`}
                h="64px"
                w="auto"
                borderRadius="sm"
                bg="white"
                p="1"
              />
            ) : null}

            <Heading size="xl" lineHeight="1.2">
              {item?.name}
            </Heading>
          </HStack>
        </HStack>

        {/* Tagline in header for stronger color hit */}
        {item?.tagline ? (
          <Text fontSize="xl" mt={2} opacity={0.95}>
            {item.tagline}
          </Text>
        ) : null}
      </Box>

      {/* BODY */}
      <Stack spacing={4} p={5}>
        {/* Specialty chips */}
        {chips.length > 0 ? (
          <HStack spacing={3} flexWrap="wrap">
            {chips.map((h) => (
              <HStack
                as="span"
                key={h}
                spacing={2}
                px={3.5}
                py={2}
                borderRadius="full"
                fontSize="md"
                fontWeight="semibold"
                lineHeight="1"
                bg={{
                  base: `${accent.chip || "blue"}.100`,
                  _dark: "whiteAlpha.200",
                }}
                color={{
                  base: `${accent.chip || "blue"}.900`,
                  _dark: "whiteAlpha.900",
                }}
              >
                <Icon as={CheckCircle} boxSize="18px" />
                <Text>{h}</Text>
              </HStack>
            ))}
          </HStack>
        ) : null}

        {/* Contact quick facts */}
        {(item?.phone || item?.siteUrl) && (
          <HStack spacing={3} flexWrap="wrap">
            {item?.phone ? (
              <HStack
                as="a"
                href={`tel:${String(item.phone).replace(/\D+/g, "")}`}
                px={4}
                py={2.5}
                borderRadius="full"
                bg={{
                  base: `${accent.chip || "blue"}.50`,
                  _dark: "whiteAlpha.200",
                }}
                borderWidth="1px"
                borderColor={{
                  base: `${accent.chip || "blue"}.200`,
                  _dark: "whiteAlpha.300",
                }}
                color={{
                  base: `${accent.chip || "blue"}.800`,
                  _dark: "whiteAlpha.900",
                }}
                _hover={{
                  bg: {
                    base: `${accent.chip || "blue"}.100`,
                    _dark: "whiteAlpha.300",
                  },
                }}
                gap={2}
              >
                <Icon as={require("lucide-react").Phone} boxSize="18px" />
                <Text fontSize="md" fontWeight="semibold">
                  {item.phone}
                </Text>
              </HStack>
            ) : null}

            {item?.siteUrl
              ? (() => {
                  let host = "";
                  try {
                    host = new URL(item.siteUrl).hostname.replace(/^www\./, "");
                  } catch {}
                  return (
                    <HStack
                      as="a"
                      href={item.siteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      px={4}
                      py={2.5}
                      borderRadius="full"
                      bg={{
                        base: `${accent.chip || "blue"}.50`,
                        _dark: "whiteAlpha.200",
                      }}
                      borderWidth="1px"
                      borderColor={{
                        base: `${accent.chip || "blue"}.200`,
                        _dark: "whiteAlpha.300",
                      }}
                      color={{
                        base: `${accent.chip || "blue"}.800`,
                        _dark: "whiteAlpha.900",
                      }}
                      _hover={{
                        bg: {
                          base: `${accent.chip || "blue"}.100`,
                          _dark: "whiteAlpha.300",
                        },
                      }}
                      gap={2}
                    >
                      <Icon
                        as={require("lucide-react").ExternalLink}
                        boxSize="18px"
                      />
                      <Text fontSize="md" fontWeight="semibold">
                        {host || "Website"}
                      </Text>
                    </HStack>
                  );
                })()
              : null}
          </HStack>
        )}

        {/* Actions */}
        <HStack spacing={3} pt={1} flexWrap="wrap">
          <Button
            as={RouterLink}
            to={`/tax-relief-companies/${item.slug || item.key}`}
            state={{ company: item }} // optional, but nice to have
            size="md"
            bgGradient={`linear(to-r, ${accent.from}, ${accent.to})`}
            color="white"
            _hover={{ opacity: 0.9 }}
          >
            Learn more
          </Button>
          <Button
            as="a"
            href={item?.siteUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            variant="outline"
            size="xl"
            colorScheme={accent.chip}
            rightIcon={<Icon as={ExternalLink} />}
          >
            Visit Their Site
          </Button>
        </HStack>

        {rating !== null ? (
          <Stack spacing={1} align="flex-end" minW="140px">
            <HStack spacing={1}>{stars}</HStack>
            <Text fontSize="xl" opacity={0.9}>
              {rating.toFixed(1)} / 5
              {item?.reviewCount ? ` • ${item.reviewCount} reviews` : ""}
            </Text>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}
